<script setup lang="ts">
import lottie from 'lottie-web/build/player/lottie_light';

import { useFlux, AppFeature, Decisions } from '~~core';

import checkAnimation from '../../assets/animation/check.json';

export type FeedbackSuccessMessageProps = {
  isFullySigned?: boolean,
  documentsCount: number,
};

const props = defineProps<FeedbackSuccessMessageProps>();

const { isFlux } = useFlux();
const animationContainer = ref<HTMLElement>();

onMounted(() => {
  if (!animationContainer.value) throw new Error('Lottie animation container not found');

  lottie.loadAnimation({
    container: animationContainer.value,
    loop: false,
    animationData: checkAnimation,
    autoplay: true,
  });
});
</script>

<template>
  <div :class="$style.wrapper">
    <div
      ref="animationContainer"
      :class="$style.animation"
      data-testid="feedbackSuccessAnimation"
    />

    <h3
      :class="$style.title"
      data-testid="feedbackSuccessTitle"
    >
      {{ $t('requirement.feedback.success.title', 1) }}
    </h3>

    <p
      v-if="!isFlux"
      :class="$style.description"
      data-testid="feedbackSuccessDescription"
    >
      <!-- SIGNER_AREA_SHORTCUT -->
      <!-- TODO: Keep only template #default block and merge with span parent node -->
      <AppFeature :name="Decisions.SIGNER_AREA_SHORTCUT">
        <template #default>
          <I18nT
            v-if="props.isFullySigned"
            tag="span"
            keypath="requirement.feedback.success.signedDescription"
            :plural="props.documentsCount"
          >
            <strong>
              {{ $t('requirement.feedback.downloadIt', props.documentsCount).toLowerCase() }}
            </strong>
          </I18nT>
          <template v-else>
            {{ $t('requirement.feedback.success.description', props.documentsCount) }}
          </template>
        </template>

        <!-- SIGNER_AREA_SHORTCUT -->
        <!-- TODO: Remove descriptionOld non-used locale -->
        <template #off>
          {{ $t('requirement.feedback.success.descriptionOld', 1) }}
        </template>
      </AppFeature>
    </p>

    <span
      v-else
      :class="$style.description"
      data-testid="feedbackFluxSuccessDescription"
    >
      {{ $t('requirement.feedback.success.facialBiometrics') }}
    </span>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: var(--space-large-xx) var(--space-medium) var(--space-small-xx);
  text-align: center;
}

.animation {
  width: 56px;
  height: 56px;
  margin-bottom: var(--space-small-xx);
}

.title {
  margin-bottom: var(--space-medium);
  font-size: var(--font-size-medium);

  @include breakpoint('small') {
    margin-bottom: var(--space-small-x);
    font-size: var(--font-size-large);
  }
}

.description {
  max-width: 810px;
  margin: 0;
  font-size: var(--font-size-regular);
}
</style>
